/** Task Force's analytics, wired to the page (App.tsx): see lib/analytics.ts and docs/ANALYTICS.md. */
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import type { TfSurface } from '@attendance-pro/shared';
import { identify, pageOf, setOverlaySurface, startAnalytics, track, trackPage } from './analytics';
import { useMe } from './session';

/**
 * Page views and tasks opened, from the address (App.tsx): a page each time the path changes (Task Force's pages
 * only), a task each time `?task=` opens one. How the person came: a push or an email (utm_source, taken out of the
 * address after), a link (the first address of the visit), or inside Task Force.
 */
export function useAnalytics() {
  const loc = useLocation();
  const navigate = useNavigate();
  const me = useMe();
  const opts = useQuery({
    queryKey: ['auth-options'],
    queryFn: () => api<{ gaId?: string }>('/api/auth/options'),
    staleTime: Infinity,
  });
  useEffect(() => {
    if (opts.data || opts.isError) startAnalytics(opts.data?.gaId);
  }, [opts.data, opts.isError]);
  useEffect(() => identify(me.data), [me.data]);
  const seen = useRef<{ path: string | null; task: string | null; first: boolean }>({
    path: null,
    task: null,
    first: true,
  });
  const ready = !me.isLoading;
  useEffect(() => {
    if (!ready) return;
    const q = new URLSearchParams(loc.search);
    const s = seen.current;
    const page = pageOf(loc.pathname);
    if (page && loc.pathname !== s.path) trackPage(page);
    s.path = loc.pathname;
    const task = q.get('task');
    const utm = q.get('utm_source');
    if (task && task !== s.task && page)
      track('task_open', {
        surface: page.surface,
        entry: utm === 'push' ? 'push' : utm === 'email' ? 'email' : s.first ? 'link' : 'in_app',
        task_ref: task,
      });
    s.task = task;
    s.first = false;
    // The address stays clean once read.
    if (utm) {
      for (const k of ['utm_source', 'utm_medium', 'utm_campaign']) q.delete(k);
      const rest = q.toString();
      navigate({ pathname: loc.pathname, search: rest ? `?${rest}` : '' }, { replace: true, state: loc.state });
    }
  }, [ready, loc.pathname, loc.search, loc.state, navigate]);
}

/** While this is open over the page (the new-task dialog, Catch up), changes made in it say so. */
export function useOverlaySurface(surface: TfSurface) {
  useEffect(() => {
    setOverlaySurface(surface);
    return () => setOverlaySurface(null);
  }, [surface]);
}

/** A search with results, counted once the words settle (only how long they are, and how many results; never them). */
export function useSearchTracking(term: string, results: number, surface: TfSurface, ready = true) {
  const last = useRef('');
  useEffect(() => {
    const t = term.trim();
    if (!t || !ready || t === last.current) return;
    const h = window.setTimeout(() => {
      last.current = t;
      track('search', { surface, term_length: t.length, results_count: results });
    }, 1200);
    return () => window.clearTimeout(h);
  }, [term, results, surface, ready]);
}

/**
 * Changes to a setting, once they settle (filters, how a view shows its tasks): never its first value, and when `scope`
 * changes (another space or view, with settings of its own) that is not a change either.
 */
export function useSettledChange<T>(value: T, scope: string, onChange: (now: T, before: T) => void, ms = 1500) {
  const key = JSON.stringify(value);
  const base = useRef<{ scope: string; key: string; value: T } | null>(null);
  const cb = useRef(onChange);
  cb.current = onChange;
  useEffect(() => {
    const b = base.current;
    if (!b || b.scope !== scope) {
      base.current = { scope, key, value };
      return;
    }
    if (b.key === key) return;
    const h = window.setTimeout(() => {
      cb.current(value, b.value);
      base.current = { scope, key, value };
    }, ms);
    return () => window.clearTimeout(h);
    // `value` is read through `key`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, scope, ms]);
}
